import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft,
  X,
  Check,
  Shield,
  Lock,
  Globe,
  ChevronRight,
  MessageCircle,
  HelpCircle,
  Info,
  Building2,
  CreditCard,
  Award,
  FileText,
  Scale,
  RefreshCw
} from "lucide-react";
import { SBER, API, pageVariants, tapAnimation, openExternalLink, tg } from "../utils/constants";
import { SberCard } from "../components/UIComponents";

const ProfilePage = ({ user, onBack, onNavigate, onUpdateUser, t, language, setLanguage, currentLanguage, languages }) => {
  const [showLegalModal, setShowLegalModal] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [pinStep, setPinStep] = useState('enter'); // 'enter' | 'confirm'
  const [pinLoading, setPinLoading] = useState(false);
  
  // License PDF URLs
  const LICENSE_URLS = {
    broker: "/documents/license-broker.pdf",
    dealer: "/documents/license-dealer.pdf"
  };
  
  const kycConfig = {
    NOT_STARTED: { labelKey: 'kyc.status.notStarted', color: SBER.gray, bg: SBER.grayPale },
    IN_PROGRESS: { labelKey: 'kyc.status.inProgress', color: '#3B82F6', bg: '#DBEAFE' },
    PENDING: { labelKey: 'kyc.status.pending', color: '#F59E0B', bg: '#FEF3C7' },
    IN_REVIEW: { labelKey: 'kyc.status.pending', color: '#F59E0B', bg: '#FEF3C7' },
    APPROVED: { labelKey: 'kyc.status.approved', color: SBER.green, bg: SBER.greenPale },
    DECLINED: { labelKey: 'kyc.status.rejected', color: '#EF4444', bg: '#FEE2E2' },
    REJECTED: { labelKey: 'kyc.status.rejected', color: '#EF4444', bg: '#FEE2E2' }
  };
  const kycStatus = kycConfig[user?.kyc_status] || kycConfig.NOT_STARTED;
  const kyc = { ...kycStatus, label: t(kycStatus.labelKey) };
  
  // Legal content
  const legalContent = {
    about: {
      title: 'О сервисе',
      content: `PolyTech Wallet — это современный валютный кошелёк для удобного хранения, обмена и перевода средств.

Сервис предоставляется ОсОО «ФВА Финанс» — лицензированным брокером и дилером на рынке ценных бумаг Кыргызской Республики.

Наши преимущества:
• Лицензированная деятельность
• Мгновенные переводы 24/7
• Низкие комиссии
• Высокий уровень безопасности
• Поддержка множества валют
• Удобный интерфейс

Мы стремимся сделать обмен валюты доступным для каждого, обеспечивая простоту использования и надёжность операций в соответствии с законодательством Кыргызской Республики.

Версия приложения: 1.0.0
Дата обновления: Январь 2025`
    },
    company: {
      title: 'О компании',
      content: `ОБЩЕСТВО С ОГРАНИЧЕННОЙ ОТВЕТСТВЕННОСТЬЮ «ФВА ФИНАНС»
ОсОО «ФВА Финанс»

РЕГИСТРАЦИОННЫЕ ДАННЫЕ:

Регистрационный номер: 150744-3301-000
Дата регистрации: 2015 год
Код ОКПО: 29284076
ИНН: 01111201510129

ЮРИДИЧЕСКИЙ АДРЕС:

720001, Кыргызская Республика, г. Бишкек,
Первомайский район, ул. Токтогула, 87A

ФАКТИЧЕСКИЙ АДРЕС:

720044, Кыргызская Республика, г. Бишкек,
Ленинский район, ул. Толомуша Океева, 39/5, кв. 27

РУКОВОДСТВО:

Директор: Уткина Екатерина Алексеевна

РЕГИСТРИРУЮЩИЙ ОРГАН:

Министерство юстиции Кыргызской Республики

ЛИЦЕНЗИИ:

Лицензия № 159 на брокерскую деятельность
Дата выдачи: 16 марта 2017 года
Срок действия: бессрочная

Лицензия № 160 на дилерскую деятельность
Дата выдачи: 16 марта 2017 года
Срок действия: бессрочная

Орган выдачи: Государственная служба регулирования и надзора за финансовым рынком при Правительстве Кыргызской Республики

БАНКОВСКИЕ РЕКВИЗИТЫ:

Банк 1: ОАО «Айыл Банк»
Р/с: 1350100020221904, БИК: 135001

Банк 2: ОАО «ФинансКредитБанк»
Р/с: 1340000088397303, БИК: 134001

Банк 3: Филиал ОАО «Керемет Банк»
Р/с: 1360370019060487, БИК: 136001

КОНТАКТЫ:

Телефон: +996 507 210 992
Email: support@polytechex.com`
    },
    terms: {
      title: 'Пользовательское соглашение',
      content: `ПОЛЬЗОВАТЕЛЬСКОЕ СОГЛАШЕНИЕ
об использовании сервиса PolyTech Wallet

Дата вступления в силу: 01.01.2025

1. ОБЩИЕ ПОЛОЖЕНИЯ

1.1. Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения между Обществом с ограниченной ответственностью «ФВА Финанс» (ИНН: 01111201510129, регистрационный номер: 150744-3301-000), расположенным по адресу: Кыргызская Республика, г. Бишкек, Первомайский район, ул. Токтогула, 87A (далее — «Оператор», «Компания», «мы»), и физическим лицом, использующим сервис PolyTech Wallet (далее — «Пользователь», «вы»).

1.2. Оператор является лицензированным брокером и дилером на рынке ценных бумаг на основании Лицензий № 159 и № 160, выданных 16 марта 2017 года Государственной службой регулирования и надзора за финансовым рынком при Правительстве Кыргызской Республики.

1.3. Использование сервиса означает полное и безоговорочное принятие условий настоящего Соглашения. Если вы не согласны с условиями, пожалуйста, прекратите использование сервиса.

2. ПРЕДМЕТ СОГЛАШЕНИЯ

2.1. Оператор предоставляет Пользователю доступ к сервису PolyTech Wallet для:
• Хранения валютных средств
• Обмена виртуальных активов на фиатные валюты и обратно
• Переводов виртуальных активов между пользователями
• Оплаты товаров и услуг
• Пополнения баланса и вывода средств

2.2. Сервис предоставляется через Telegram-бота и веб-приложение (Mini App).

3. ПРАВА И ОБЯЗАННОСТИ ПОЛЬЗОВАТЕЛЯ

3.1. Пользователь обязуется:
• Предоставлять достоверную информацию при регистрации и верификации
• Соблюдать законодательство Кыргызской Республики и страны своего резидентства
• Не использовать сервис в противоправных целях
• Обеспечивать конфиденциальность своих учётных данных и PIN-кода
• Незамедлительно уведомлять Оператора о несанкционированном доступе

3.2. Пользователь имеет право:
• Использовать все функции сервиса в соответствии с уровнем верификации
• Получать информацию о своих операциях
• Обращаться в службу поддержки
• Удалить свой аккаунт

4. ПРАВА И ОБЯЗАННОСТИ ОПЕРАТОРА

4.1. Оператор обязуется:
• Обеспечивать работоспособность сервиса
• Защищать персональные данные Пользователя
• Своевременно информировать об изменениях условий
• Осуществлять деятельность в соответствии с лицензией

4.2. Оператор имеет право:
• Приостановить или ограничить доступ к сервису при нарушении условий
• Запрашивать дополнительные документы для верификации
• Изменять тарифы и условия с предварительным уведомлением
• Отказать в обслуживании в случаях, предусмотренных законодательством

5. ОГРАНИЧЕНИЕ ОТВЕТСТВЕННОСТИ

5.1. Оператор не несёт ответственности за:
• Убытки, связанные с колебаниями курсов виртуальных активов
• Действия третьих лиц
• Технические сбои сетей и блокчейнов
• Последствия нарушения Пользователем условий Соглашения

6. ПРИМЕНИМОЕ ПРАВО

6.1. Настоящее Соглашение регулируется законодательством Кыргызской Республики.

6.2. Все споры разрешаются путём переговоров, а при недостижении согласия — в судебном порядке по месту нахождения Оператора.

7. ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ

7.1. Соглашение вступает в силу с момента начала использования сервиса.

7.2. Оператор вправе в одностороннем порядке изменять условия Соглашения, уведомив Пользователя не менее чем за 7 дней.

ОсОО «ФВА Финанс»
ИНН: 01111201510129
Лицензии: № 159, № 160 от 16.03.2017
Адрес: Кыргызская Республика, г. Бишкек, ул. Токтогула, 87A`
    },
    privacy: {
      title: 'Политика конфиденциальности',
      content: `ПОЛИТИКА КОНФИДЕНЦИАЛЬНОСТИ
ОсОО «ФВА Финанс»

Дата вступления в силу: 01.01.2025

1. ОБЩИЕ ПОЛОЖЕНИЯ

1.1. Настоящая Политика конфиденциальности описывает порядок сбора, использования, хранения и защиты персональных данных Пользователей сервиса PolyTech Wallet.

1.2. Оператор персональных данных: Общество с ограниченной ответственностью «ФВА Финанс»
ИНН: 01111201510129
Адрес: Кыргызская Республика, г. Бишкек, Первомайский район, ул. Токтогула, 87A
Лицензии: № 159, № 160 от 16.03.2017

2. СБОР ПЕРСОНАЛЬНЫХ ДАННЫХ

2.1. Мы собираем следующую информацию:

Данные Telegram:
• Идентификатор пользователя (Telegram ID)
• Имя пользователя (username)
• Имя и фамилия (при наличии)

Данные верификации (KYC):
• Документ, удостоверяющий личность
• Фотография (селфи)
• Адрес проживания
• Номер телефона

Данные о транзакциях:
• История операций
• Суммы и направления переводов
• Адреса криптокошельков

Техническая информация:
• IP-адрес
• Тип устройства и браузера
• Время доступа к сервису

3. ЦЕЛИ ОБРАБОТКИ ДАННЫХ

3.1. Собранная информация используется для:
• Предоставления услуг сервиса
• Идентификации и верификации пользователей
• Выполнения требований AML/KYC
• Обеспечения безопасности
• Предотвращения мошенничества
• Улучшения качества сервиса
• Выполнения требований законодательства
• Связи с пользователем

4. ЗАЩИТА ДАННЫХ

4.1. Мы применяем следующие меры защиты:
• Шифрование данных при передаче (TLS/SSL)
• Шифрование данных при хранении
• Многофакторная аутентификация
• Контроль и разграничение доступа
• Регулярный аудит безопасности
• Резервное копирование данных

5. ПЕРЕДАЧА ДАННЫХ ТРЕТЬИМ ЛИЦАМ

5.1. Мы не продаём и не передаём персональные данные третьим лицам, за исключением случаев:
• Требований законодательства Кыргызской Республики
• Запросов уполномоченных государственных органов
• Необходимости исполнения договорных обязательств
• Явного согласия пользователя

5.2. Партнёры и подрядчики, имеющие доступ к данным, обязаны соблюдать конфиденциальность.

6. ХРАНЕНИЕ ДАННЫХ

6.1. Персональные данные хранятся:
• В течение срока действия аккаунта
• 5 лет после удаления аккаунта (в соответствии с требованиями AML)
• В соответствии с требованиями законодательства

7. ПРАВА ПОЛЬЗОВАТЕЛЯ

7.1. Вы имеете право:
• Получить доступ к своим персональным данным
• Исправить неточные или неполные данные
• Удалить данные (с учётом законодательных ограничений)
• Ограничить обработку данных
• Отозвать согласие на обработку
• Подать жалобу в уполномоченный орган

8. КОНТАКТНАЯ ИНФОРМАЦИЯ

По вопросам обработки персональных данных обращайтесь:
Email: support@polytechex.com
Телефон: +996 507 210 992

ОсОО «ФВА Финанс»
ИНН: 01111201510129
Адрес: Кыргызская Республика, г. Бишкек, ул. Токтогула, 87A`
    },
    aml: {
      title: 'AML/KYC Политика',
      content: `ПОЛИТИКА AML/KYC
Противодействие легализации доходов и идентификация клиентов

ОсОО «ФВА Финанс»
Лицензии: № 159, № 160 от 16.03.2017
Государственная служба регулирования и надзора за финансовым рынком при Правительстве Кыргызской Республики

Дата вступления в силу: 01.01.2025

1. ОБЩИЕ ПОЛОЖЕНИЯ

1.1. Настоящая Политика разработана в соответствии с:
• Законодательством Кыргызской Республики о противодействии легализации (отмыванию) доходов, полученных преступным путём, и финансированию терроризма
• Требованиями Государственной службы регулирования и надзора за финансовым рынком
• Международными стандартами FATF
• Условиями лицензий на брокерскую и дилерскую деятельность

1.2. ОсОО «ФВА Финанс» (ИНН: 01111201510129, рег. номер: 150744-3301-000) обязуется неукоснительно соблюдать все требования AML/KYC.

2. ИДЕНТИФИКАЦИЯ КЛИЕНТОВ (KYC)

2.1. Уровни верификации:

УРОВЕНЬ 1 — БАЗОВЫЙ
Требования:
• Telegram аккаунт
• Установка PIN-кода
Лимиты:
• Пополнение: до 200 USD/день
• Вывод: недоступен
• Обмен: до 200 USD/день

УРОВЕНЬ 2 — СТАНДАРТНЫЙ
Требования:
• Документ, удостоверяющий личность (паспорт, ID-карта)
• Селфи с документом
• Номер телефона
Лимиты:
• Пополнение: до 1 000 USD/день
• Вывод: до 1 000 USD/день
• Обмен: до 1 000 USD/день

УРОВЕНЬ 3 — РАСШИРЕННЫЙ
Требования:
• Подтверждение адреса (коммунальные счета, выписка из банка)
• Подтверждение источника средств
• Дополнительная проверка
Лимиты:
• Без ограничений (индивидуально)

3. МОНИТОРИНГ ТРАНЗАКЦИЙ

3.1. Автоматический мониторинг:
• Крупные транзакции (свыше 10 000 USD или эквивалент)
• Множественные транзакции за короткий период
• Транзакции с высокорисковыми юрисдикциями
• Нетипичная активность для профиля клиента

3.2. Подозрительные операции:
• Дробление сумм для обхода лимитов
• Несоответствие операций заявленному источнику дохода
• Использование анонимизирующих сервисов
• Связь с адресами из санкционных списков

4. ЗАПРЕЩЁННЫЕ ДЕЙСТВИЯ

4.1. Сервис не может быть использован для:
• Легализации доходов, полученных преступным путём
• Финансирования терроризма
• Обхода международных санкций
• Любой противоправной деятельности

5. ОТКАЗ В ОБСЛУЖИВАНИИ

5.1. Мы оставляем за собой право отказать в обслуживании или заблокировать аккаунт при:
• Предоставлении ложных или поддельных документов
• Подозрении в противоправной деятельности
• Нахождении в санкционных списках
• Отказе от прохождения верификации
• Нарушении условий использования сервиса

6. ХРАНЕНИЕ ДАННЫХ

6.1. В соответствии с требованиями законодательства:
• Данные KYC хранятся минимум 5 лет после закрытия аккаунта
• Записи о транзакциях хранятся минимум 5 лет
• Данные предоставляются по запросу уполномоченных органов

7. СОТРУДНИЧЕСТВО С ГОСУДАРСТВЕННЫМИ ОРГАНАМИ

7.1. ОсОО «ФВА Финанс» сотрудничает с:
• Государственной службой регулирования и надзора за финансовым рынком при Правительстве КР
• Государственной службой финансовой разведки КР
• Правоохранительными органами в рамках законодательства

7.2. Сообщения о подозрительных операциях направляются в уполномоченные органы в установленные сроки.

8. КОНТАКТНАЯ ИНФОРМАЦИЯ

Ответственное лицо по AML/KYC:
Email: support@polytechex.com
Телефон: +996 507 210 992

ОсОО «ФВА Финанс»
ИНН: 01111201510129
Рег. номер: 150744-3301-000
Лицензии: № 159, № 160 от 16.03.2017
Адрес: Кыргызская Республика, г. Бишкек, ул. Токтогула, 87A`
    },
    fees: {
      title: 'Тарифы и комиссии',
      content: `ТАРИФЫ И КОМИССИИ
ОсОО «ФВА Финанс»

Дата обновления: 01.02.2025

ПОПОЛНЕНИЕ БАЛАНСА

Банковский перевод (USD):
• Комиссия: 1%
• Минимум: 10 USD
• Максимум: 10 000 USD
• Время зачисления: 1-24 часа

Банковский перевод (KGS):
• Комиссия: 1.5%
• Минимум: 500 KGS
• Максимум: 500 000 KGS
• Время зачисления: 1-24 часа

ВЫВОД СРЕДСТВ

На банковскую карту (USD):
• Комиссия: 2%
• Минимум: 20 USD
• Время: 1-24 часа

На банковскую карту (KGS):
• Комиссия: 2.5%
• Минимум: 1 000 KGS
• Время: 1-24 часа

На валютный счёт (USD):
• Комиссия: 1%
• Минимум: 10 USD
• Время: 1-30 минут

ОБМЕН ВАЛЮТ

USD ↔ KGS:
• Комиссия: 1.5%
• Курс: рыночный + спред 0.5%

USD ↔ EUR:
• Комиссия: 1%
• Курс: рыночный + спред 0.3%

USD ↔ MDL:
• Комиссия: 0.7%
• Курс: BNM + спред 0.5%

USD ↔ KZT:
• Комиссия: 1.5%
• Курс: рыночный + спред 0.5%

ПЛАТЕЖИ И ПЕРЕВОДЫ

Мобильная связь:
• Комиссия: 3%

ЛИМИТЫ ПО УРОВНЯМ ВЕРИФИКАЦИИ

Уровень 1 (Базовый):
• Пополнение: до 200 USD/день
• Вывод: недоступен

Уровень 2 (Стандартный - после KYC):
• Пополнение: до 1 000 USD/день
• Вывод: до 1 000 USD/день

Уровень 3 (Расширенный):
• Пополнение: до 10 000 USD/день
• Вывод: до 10 000 USD/день

Примечание: Тарифы могут быть изменены. Актуальные тарифы отображаются при совершении операции.

ОсОО «ФВА Финанс»
ИНН: 01111201510129
Лицензии: № 159, № 160`
    },
    license: {
      title: 'Лицензия',
      content: `ЛИЦЕНЗИИ НА ОСУЩЕСТВЛЕНИЕ ДЕЯТЕЛЬНОСТИ
НА РЫНКЕ ЦЕННЫХ БУМАГ

ЛИЦЕНЗИЯ № 159
на осуществление брокерской деятельности на рынке ценных бумаг

ЛИЦЕНЗИЯ № 160
на осуществление дилерской деятельности на рынке ценных бумаг

ЛИЦЕНЗИАТ:
Общество с ограниченной ответственностью «ФВА Финанс»
ИНН: 01111201510129
Регистрационный номер: 150744-3301-000

ОРГАН, ВЫДАВШИЙ ЛИЦЕНЗИИ:
Государственная служба регулирования и надзора за финансовым рынком при Правительстве Кыргызской Республики

ДАТА ВЫДАЧИ:
16 марта 2017 года

ВИД ДЕЯТЕЛЬНОСТИ:
Брокерская и дилерская деятельность на рынке ценных бумаг

СРОК ДЕЙСТВИЯ:
Бессрочная

АДРЕС ЛИЦЕНЗИАТА:
720001, Кыргызская Республика, г. Бишкек,
Первомайский район, ул. Токтогула, 87A

Нажмите кнопку ниже для просмотра скана лицензии.`
    }
  };
  
  // Legal Modal Component
  const LegalModal = ({ type, onClose }) => {
    const content = legalContent[type];
    if (!content) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end justify-center"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-[430px] max-h-[85vh] rounded-t-3xl overflow-hidden"
          style={{ background: SBER.white }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: SBER.grayPale }}>
            <h2 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>{content.title}</h2>
            <motion.button
              whileTap={tapAnimation}
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: SBER.grayPale }}
            >
              <X className="w-4 h-4" style={{ color: SBER.gray }} />
            </motion.button>
          </div>
          
          {/* Modal Content */}
          <div className="px-5 py-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
            <pre className="whitespace-pre-wrap font-sans text-[14px] leading-relaxed" style={{ color: SBER.dark }}>
              {content.content}
            </pre>
            
            {/* License PDF Buttons */}
            {type === 'license' && (
              <div className="space-y-3 mt-4">
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => openExternalLink(LICENSE_URLS.broker)}
                  className="w-full py-4 rounded-xl font-semibold text-[16px] text-white flex items-center justify-center gap-2"
                  style={{ background: SBER.green, boxShadow: '0 4px 16px rgba(33, 160, 56, 0.3)' }}
                >
                  <FileText className="w-5 h-5" />
                  Лицензия на брокерскую деятельность (PDF)
                </motion.button>
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => openExternalLink(LICENSE_URLS.dealer)}
                  className="w-full py-4 rounded-xl font-semibold text-[16px] text-white flex items-center justify-center gap-2"
                  style={{ background: SBER.green, boxShadow: '0 4px 16px rgba(33, 160, 56, 0.3)' }}
                >
                  <FileText className="w-5 h-5" />
                  Лицензия на дилерскую деятельность (PDF)
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  };
  
  // Menu Item Component
  const MenuItem = ({ icon: Icon, label, onClick, color = SBER.gray }) => (
    <motion.button
      whileTap={tapAnimation}
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4"
    >
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: SBER.grayPale }}
      >
        <Icon className="w-5 h-5" style={{ color }} strokeWidth={2} />
      </div>
      <span className="flex-1 text-left font-medium text-[15px]" style={{ color: SBER.dark }}>{label}</span>
      <ChevronRight className="w-5 h-5" style={{ color: SBER.grayLight }} />
    </motion.button>
  );
  
  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-28"
      style={{ background: SBER.bg }}
    >
      <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
        <motion.button 
          whileTap={tapAnimation}
          onClick={onBack}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: SBER.grayPale }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
        </motion.button>
        <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
          {t('profile.title')}
        </h1>
      </div>
      
      <div className="px-5 pt-5">
        {/* User Card */}
        <SberCard className="p-5 mb-4">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold"
              style={{ background: SBER.green }}
            >
              {(user?.first_name?.[0] || 'U').toUpperCase()}
            </div>
            <div>
              <p className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
                {user?.first_name || 'User'}
              </p>
              <p className="text-[14px]" style={{ color: SBER.green }}>@{user?.username || 'wallet'}</p>
              <p className="text-[12px] mt-0.5" style={{ color: SBER.grayLight }}>ID: {user?.telegram_id}</p>
            </div>
          </div>
        </SberCard>
        
        {/* KYC Status */}
        <SberCard className="p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: SBER.greenPale }}
              >
                <Shield className="w-5 h-5" style={{ color: SBER.green }} strokeWidth={2} />
              </div>
              <span className="font-medium" style={{ color: SBER.dark }}>{t('kyc.verification')}</span>
            </div>
            <span 
              className="px-3 py-1 rounded-full text-[13px] font-medium"
              style={{ background: kyc.bg, color: kyc.color }}
            >
              {kyc.label}
            </span>
          </div>
          
          {user?.kyc_status !== 'APPROVED' && (
            <motion.button
              whileTap={tapAnimation}
              onClick={async () => {
                try {
                  const loadingToast = toast.loading(t('kyc.creatingSession'));
                  const response = await fetch(`${API}/kyc/session`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('wallet_token') || ''}`,
                      'X-Telegram-Id': user?.telegram_id?.toString() || ''
                    },
                    body: JSON.stringify({})
                  });
                  const data = await response.json();
                  toast.dismiss(loadingToast);
                  
                  if (data.success && data.verification_url) {
                    // Try to open verification link
                    const url = data.verification_url;
                    
                    // Try Telegram openLink first
                    if (tg && typeof tg.openLink === 'function') {
                      try {
                        tg.openLink(url, { try_instant_view: false });
                        toast.success(t('kyc.openedInBrowser') || 'Откройте браузер для верификации');
                      } catch (e) {
                        // If openLink fails, show copyable link
                        console.error('tg.openLink failed:', e);
                        toast.info(
                          <div onClick={() => {
                            navigator.clipboard.writeText(url);
                            toast.success('Ссылка скопирована!');
                          }}>
                            <p>Нажмите чтобы скопировать ссылку:</p>
                            <p className="text-xs break-all mt-1">{url.substring(0, 50)}...</p>
                          </div>,
                          { duration: 10000 }
                        );
                      }
                    } else {
                      // Not in Telegram or openLink not available
                      const newWindow = window.open(url, '_blank');
                      if (newWindow) {
                        toast.success('Откройте новую вкладку для верификации');
                      } else {
                        // Popup blocked, show link
                        toast.info(
                          <div onClick={() => {
                            navigator.clipboard.writeText(url);
                            toast.success('Ссылка скопирована!');
                          }}>
                            <p>Нажмите чтобы скопировать ссылку:</p>
                            <p className="text-xs break-all mt-1">{url.substring(0, 50)}...</p>
                          </div>,
                          { duration: 10000 }
                        );
                      }
                    }
                    
                    // Update local user state
                    if (onUpdateUser) onUpdateUser(prev => ({ ...prev, kyc_status: 'IN_PROGRESS' }));
                  } else if (data.kyc_status === 'APPROVED') {
                    toast.success('Верификация уже пройдена!');
                    if (onUpdateUser) onUpdateUser(prev => ({ ...prev, kyc_status: 'APPROVED' }));
                  } else {
                    toast.error(data.detail || data.error || 'Ошибка создания сессии');
                  }
                } catch (error) {
                  console.error('KYC session error:', error);
                  toast.error('Ошибка подключения к сервису');
                }
              }}
              className="w-full py-3 rounded-xl text-white font-semibold"
              style={{ background: SBER.green }}
              data-testid="start-kyc-btn"
            >
              {user?.kyc_status === 'IN_PROGRESS' || user?.kyc_status === 'PENDING' 
                ? t('kyc.continueKYC') 
                : t('kyc.passKYC')}
            </motion.button>
          )}
        </SberCard>
        
        {/* PIN Status */}
        <SberCard className="p-5 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: user?.has_pin ? SBER.greenPale : SBER.grayPale }}
              >
                <Lock className="w-5 h-5" style={{ color: user?.has_pin ? SBER.green : SBER.gray }} strokeWidth={2} />
              </div>
              <span className="font-medium" style={{ color: SBER.dark }}>{t('profile.pin.title')}</span>
            </div>
            <span 
              className="px-3 py-1 rounded-full text-[13px] font-medium"
              style={{ 
                background: user?.has_pin ? SBER.greenPale : SBER.grayPale, 
                color: user?.has_pin ? SBER.green : SBER.gray 
              }}
            >
              {user?.has_pin ? t('profile.pin.set') : t('profile.pin.notSet')}
            </span>
          </div>
          
          <motion.button
            whileTap={tapAnimation}
            onClick={() => {
              setShowPinModal(true);
              setPin(['', '', '', '']);
              setConfirmPin(['', '', '', '']);
              setPinStep('enter');
            }}
            className="w-full py-3 rounded-xl font-semibold"
            style={{ 
              background: user?.has_pin ? SBER.grayPale : SBER.green,
              color: user?.has_pin ? SBER.dark : SBER.white
            }}
          >
            {user?.has_pin ? t('profile.pin.changePin') : t('profile.pin.setPin')}
          </motion.button>
        </SberCard>
        
        {/* Language Section */}
        <SberCard className="p-5 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: SBER.greenPale }}
              >
                <Globe className="w-5 h-5" style={{ color: SBER.green }} strokeWidth={2} />
              </div>
              <span className="font-medium" style={{ color: SBER.dark }}>{t('profile.language.title')}</span>
            </div>
            <motion.button
              whileTap={tapAnimation}
              onClick={() => setShowLanguageModal(true)}
              className="px-4 py-2 rounded-xl flex items-center gap-2"
              style={{ background: SBER.grayPale }}
              data-testid="language-selector-btn"
            >
              <span className="text-lg">{currentLanguage?.flag}</span>
              <span className="text-[14px] font-medium" style={{ color: SBER.dark }}>
                {currentLanguage?.name}
              </span>
              <ChevronRight className="w-4 h-4" style={{ color: SBER.grayLight }} />
            </motion.button>
          </div>
        </SberCard>
        
        {/* Support Section */}
        <p className="text-[13px] font-semibold mb-2 mt-6" style={{ color: SBER.gray }}>
          {t('support.title')}
        </p>
        <SberCard className="mb-4 overflow-hidden">
          <MenuItem 
            icon={MessageCircle} 
            label={t('support.aiAssistant')} 
            onClick={() => onNavigate('support-chat')}
            color={SBER.green}
            data-testid="support-chat-btn"
          />
          <div className="h-px mx-4" style={{ background: SBER.grayPale }} />
          <MenuItem 
            icon={HelpCircle} 
            label={t('support.faq')} 
            onClick={() => toast.info(t('support.inDevelopment'))}
          />
        </SberCard>
        
        {/* Legal Section */}
        <p className="text-[13px] font-semibold mb-2 mt-6" style={{ color: SBER.gray }}>
          {t('info.title')}
        </p>
        <SberCard className="mb-4 overflow-hidden">
          <MenuItem 
            icon={Info} 
            label={t('info.aboutService')} 
            onClick={() => setShowLegalModal('about')}
          />
          <div className="h-px mx-4" style={{ background: SBER.grayPale }} />
          <MenuItem 
            icon={Building2} 
            label={t('info.aboutCompany')} 
            onClick={() => setShowLegalModal('company')}
          />
          <div className="h-px mx-4" style={{ background: SBER.grayPale }} />
          <MenuItem 
            icon={CreditCard} 
            label={t('info.tariffs')} 
            onClick={() => setShowLegalModal('fees')}
          />
        </SberCard>
        
        {/* Legal Documents */}
        <p className="text-[13px] font-semibold mb-2 mt-6" style={{ color: SBER.gray }}>
          {t('legal.title')}
        </p>
        <SberCard className="mb-4 overflow-hidden">
          <MenuItem 
            icon={Award} 
            label={t('legal.license')} 
            onClick={() => setShowLegalModal('license')}
            color={SBER.green}
          />
          <div className="h-px mx-4" style={{ background: SBER.grayPale }} />
          <MenuItem 
            icon={FileText} 
            label={t('legal.terms')} 
            onClick={() => setShowLegalModal('terms')}
          />
          <div className="h-px mx-4" style={{ background: SBER.grayPale }} />
          <MenuItem 
            icon={Lock} 
            label={t('legal.privacy')} 
            onClick={() => setShowLegalModal('privacy')}
          />
          <div className="h-px mx-4" style={{ background: SBER.grayPale }} />
          <MenuItem 
            icon={Scale} 
            label={t('legal.aml')} 
            onClick={() => setShowLegalModal('aml')}
          />
        </SberCard>
        
        {/* App Version */}
        <div className="text-center mt-6 mb-4">
          <p className="text-[13px]" style={{ color: SBER.grayLight }}>
            PolyTech Wallet v1.0.0
          </p>
          <p className="text-[12px] mt-1" style={{ color: SBER.grayLight }}>
            {t('general.copyright')}
          </p>
        </div>
      </div>
      
      {/* Legal Modal */}
      <AnimatePresence>
        {showLegalModal && (
          <LegalModal type={showLegalModal} onClose={() => setShowLegalModal(null)} />
        )}
      </AnimatePresence>
      
      {/* PIN Modal */}
      <AnimatePresence>
        {showPinModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-5"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowPinModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-[350px] rounded-3xl p-6"
              style={{ background: SBER.white }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[20px] font-bold" style={{ color: SBER.dark }}>
                  {pinStep === 'enter' ? 'Введите PIN-код' : 'Повторите PIN-код'}
                </h2>
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => setShowPinModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: SBER.grayPale }}
                >
                  <X className="w-4 h-4" style={{ color: SBER.gray }} />
                </motion.button>
              </div>
              
              <p className="text-[14px] text-center mb-6" style={{ color: SBER.gray }}>
                {pinStep === 'enter' 
                  ? 'Придумайте 4-значный PIN-код для защиты операций' 
                  : 'Введите PIN-код ещё раз для подтверждения'}
              </p>
              
              {/* PIN Input Display */}
              <div className="flex justify-center gap-4 mb-8">
                {(pinStep === 'enter' ? pin : confirmPin).map((digit, idx) => (
                  <div
                    key={idx}
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-[24px] font-bold"
                    style={{ 
                      background: digit ? SBER.greenPale : SBER.grayPale,
                      color: SBER.green,
                      border: `2px solid ${digit ? SBER.green : 'transparent'}`
                    }}
                  >
                    {digit ? '•' : ''}
                  </div>
                ))}
              </div>
              
              {/* Number Pad */}
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'delete'].map((num, idx) => (
                  <motion.button
                    key={idx}
                    whileTap={num !== null ? tapAnimation : undefined}
                    onClick={async () => {
                      if (num === null) return;
                      
                      const currentPin = pinStep === 'enter' ? pin : confirmPin;
                      const setCurrentPin = pinStep === 'enter' ? setPin : setConfirmPin;
                      
                      if (num === 'delete') {
                        const newPin = [...currentPin];
                        const lastFilledIndex = newPin.findLastIndex(d => d !== '');
                        if (lastFilledIndex >= 0) {
                          newPin[lastFilledIndex] = '';
                          setCurrentPin(newPin);
                        }
                      } else {
                        const emptyIndex = currentPin.findIndex(d => d === '');
                        if (emptyIndex >= 0) {
                          const newPin = [...currentPin];
                          newPin[emptyIndex] = String(num);
                          setCurrentPin(newPin);
                          
                          // Check if PIN is complete
                          if (emptyIndex === 3) {
                            if (pinStep === 'enter') {
                              setTimeout(() => setPinStep('confirm'), 300);
                            } else {
                              // Verify PINs match
                              const enteredPin = pin.join('');
                              const confirmedPin = newPin.join('');
                              
                              if (enteredPin === confirmedPin) {
                                setPinLoading(true);
                                // Call API to set PIN
                                try {
                                  const res = await axios.post(`${API}/user/set_pin`, {
                                    telegram_id: user.telegram_id || tg?.initDataUnsafe?.user?.id,
                                    pin: enteredPin
                                  });
                                  setPinLoading(false);
                                  if (res.data.success) {
                                    setShowPinModal(false);
                                    toast.success('PIN-код успешно установлен!');
                                    // Update user state
                                    if (onUpdateUser) {
                                      onUpdateUser({ ...user, has_pin: true });
                                    }
                                  } else {
                                    toast.error(res.data.message || 'Ошибка установки PIN');
                                  }
                                } catch (err) {
                                  setPinLoading(false);
                                  toast.error('Ошибка подключения к серверу');
                                }
                              } else {
                                toast.error(t('profile.pin.pinMismatch'));
                                setConfirmPin(['', '', '', '']);
                              }
                            }
                          }
                        }
                      }
                    }}
                    className={`h-14 rounded-xl flex items-center justify-center text-[20px] font-semibold transition-all ${
                      num === null ? 'opacity-0 pointer-events-none' : ''
                    }`}
                    style={{ 
                      background: num === 'delete' ? 'transparent' : SBER.grayPale,
                      color: SBER.dark
                    }}
                    disabled={pinLoading}
                  >
                    {num === 'delete' ? (
                      <ArrowLeft className="w-6 h-6" style={{ color: SBER.gray }} />
                    ) : num}
                  </motion.button>
                ))}
              </div>
              
              {pinLoading && (
                <div className="flex items-center justify-center mt-6">
                  <RefreshCw className="w-6 h-6 animate-spin" style={{ color: SBER.green }} />
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Language Selection Modal */}
      <AnimatePresence>
        {showLanguageModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={() => setShowLanguageModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-[430px] rounded-t-3xl overflow-hidden"
              style={{ background: SBER.white }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: SBER.grayPale }}>
                <h2 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>{t('profile.language.select')}</h2>
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => setShowLanguageModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: SBER.grayPale }}
                >
                  <X className="w-4 h-4" style={{ color: SBER.gray }} />
                </motion.button>
              </div>
              
              {/* Language Options */}
              <div className="p-4 space-y-2">
                {languages.map((lang) => (
                  <motion.button
                    key={lang.code}
                    whileTap={tapAnimation}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowLanguageModal(false);
                      toast.success(`${lang.flag} ${lang.name}`);
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-xl transition-all"
                    style={{ 
                      background: language === lang.code ? SBER.greenPale : SBER.grayPale,
                      border: language === lang.code ? `2px solid ${SBER.green}` : '2px solid transparent'
                    }}
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="flex-1 text-left font-medium text-[16px]" style={{ color: SBER.dark }}>
                      {lang.name}
                    </span>
                    {language === lang.code && (
                      <Check className="w-5 h-5" style={{ color: SBER.green }} />
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProfilePage;
